import React, { type ComponentType, type ReactNode, Suspense, useEffect } from "react";

import { logger } from "@jslib/shared/utils/logger";

import {
  type VerificationStep,
  type VerificationService,
  type OverrideableComponentName,
} from "../lib/types/types";
import { getOverridenComponent } from "../lib/componentOverride/componentOverride";
import { CustomMetricNames, EventName, recordGaEvent } from "../lib/GoogleAnalytics/ga";

import { LoadingScreenComponent } from "./LoadingScreen/LoadingScreenComponent";

// These should _not_ be lazy loaded
// Either they should be immediately available (e.g. loading screen)
// or they are very frequently used, so much so that we want to avoid
// an extra round-trip to load them
import StepPendingComponent from "./StepPending/index";
import StepErrorComponent from "./StepError";

const defaultComponents: Record<
  OverrideableComponentName,
  ComponentType | ((props: unknown) => ReactNode)
> = {
  StepPendingComponent,
  StepErrorComponent,
  StepStudentPersonalInfoComponent: React.lazy(() => import("./StepStudentPersonalInfo")),
  StepOverrideComponent: React.lazy(() => import("./StepOverrideComponent")),
  StepAgePersonalInfoComponent: React.lazy(() => import("./StepAgePersonalInfo")),
  StepSeniorPersonalInfoComponent: React.lazy(() => import("./StepAgePersonalInfo")),
  StepCollectMilitaryPersonalInfoComponent: React.lazy(
    () => import("./StepMilitaryPersonalInfo/StepCollectMilitaryPersonalInfoComponent"),
  ),
  // Soft-deprecated aliases — same component, kept for one release so
  // existing componentOverrides registrations keep working.
  StepCollectMilitaryStatusComponent: React.lazy(
    () => import("./StepMilitaryPersonalInfo/StepCollectMilitaryPersonalInfoComponent"),
  ),
  StepActiveMilitaryPersonalInfoComponent: React.lazy(
    () => import("./StepMilitaryPersonalInfo/StepCollectMilitaryPersonalInfoComponent"),
  ),
  StepInactiveMilitaryPersonalInfoComponent: React.lazy(
    () => import("./StepMilitaryPersonalInfo/StepCollectMilitaryPersonalInfoComponent"),
  ),
  StepDriverLicensePersonalInfoComponent: React.lazy(
    () => import("./StepIdentityPersonalInfo/StepDriverLicensePersonalInfoComponent"),
  ),
  StepEmploymentPersonalInfoComponent: React.lazy(() => import("./StepEmploymentPersonalInfo")),
  StepFirstResponderPersonalInfoComponent: React.lazy(
    () => import("./StepFirstResponderPersonalInfo/index"),
  ),
  StepGeneralIdentityPersonalInfoComponent: React.lazy(
    () => import("./StepIdentityPersonalInfo/StepGeneralIdentityPersonalInfoComponent"),
  ),
  StepHybridIdentityPersonalInfoComponent: React.lazy(
    () => import("./StepIdentityPersonalInfo/StepHybridIdentityPersonalInfoComponent"),
  ),
  StepIDCheckLoopComponent: React.lazy(() => import("./StepIDCheckLoop")),
  StepLicensedProfessionalPersonalInfoComponent: React.lazy(
    () => import("./StepLicensedProfessionalPersonalInfo/index"),
  ),
  StepLowIncomePersonalInfoComponent: React.lazy(() => import("./StepLowIncomePersonalInfo")),
  StepMedicalProfessionalPersonalInfoComponent: React.lazy(
    () => import("./StepMedicalProfessionalPersonalInfo"),
  ),
  StepMemberPersonalInfoComponent: React.lazy(() => import("./StepMemberPersonalInfo")),
  StepConsolationComponent: React.lazy(() => import("./StepConsolation")),
  StepDocUploadComponent: React.lazy(() => import("./StepDocUpload")),
  StepEmailLoopComponent: React.lazy(() => import("./StepEmailLoop")),
  StepPendingEmailLoopComponent: React.lazy(() => import("./StepEmailLoop/EmailLoopPending")),
  StepMoverPersonalInfoComponent: React.lazy(() => import("./StepMoverPersonalInfo")),
  StepSMSLoopComponent: React.lazy(() => import("./StepSMSLoop")),
  StepSocialSecurity: React.lazy(() => import("./StepSocialSecurityNumber")),
  StepSSOComponent: React.lazy(() => import("./StepSSO")),
  StepSuccessComponent: React.lazy(() => import("./StepSuccess")),
  StepTeacherPersonalInfoComponent: React.lazy(() => import("./StepTeacherPersonalInfo")),
  StepCompleteAuthentication: React.lazy(() => import("./StepCompleteAuthentication/index")),
  StepCollectIdentifierComponent: React.lazy(() => import("./StepCollectIdentifier")),
  StepCollectPersonalInfo: React.lazy(() => import("./StepCollectPersonalInfo")),
  StepThirdPartyLoopComponent: React.lazy(() => import("./StepThirdPartyLoop")),
};

const getComponent = (componentName: OverrideableComponentName) => {
  logger.info("getComponent", componentName);
  const overriddenComponent = getOverridenComponent(componentName);
  if (overriddenComponent) {
    return overriddenComponent;
  }

  return defaultComponents[componentName];
};

const getLoadingScreen = () => <LoadingScreenComponent />;

interface Props {
  stepToLoad: VerificationStep;
  verificationService: VerificationService;
}

const LoadStepComponent = ({ stepToLoad, verificationService }: Props) => {
  logger.info(`LoadStepComponent: rendering step: ${stepToLoad}`);
  let Component;

  useEffect(() => {
    // see ObservePerformanceMetrics in ga.ts - we'll use LCP if we have it as
    // it's a better metric, but this gives us something in all browsers.
    try {
      performance.mark(CustomMetricNames.INITIAL_STEP_LOAD);
    } catch {
      // jsdom doesn't have `performance` as such tests fall over.
    }
  }, []);

  // To allow for a ratio of form_seen:form_submitted events. Help track system & program health.
  React.useEffect(() => {
    // don't spam offsite logging
    if (["pending", "error"].includes(stepToLoad)) {
      return;
    }
    try {
      // if this happens repeatedly, the last mark will be used when .measure() is called:
      performance.mark(EventName.STEP_SEEN);

      // if this happens repeatedly remember you can group by verificationId in BI queries:
      recordGaEvent({
        eventName: EventName.STEP_SEEN,
        params: {
          step: stepToLoad,
        },
      });
    } catch (e) {
      logger.error(e);
    }
  }, [stepToLoad]);

  switch (stepToLoad) {
    case "collectPersonalInfo":
      Component = getComponent("StepCollectPersonalInfo");
      break;
    case "collectStudentPersonalInfo":
      Component = getComponent("StepStudentPersonalInfoComponent");
      break;
    case "collectSeniorPersonalInfo":
      Component = getComponent("StepSeniorPersonalInfoComponent");
      break;
    case "collectAgePersonalInfo":
      Component = getComponent("StepAgePersonalInfoComponent");
      break;
    case "collectTeacherPersonalInfo":
      Component = getComponent("StepTeacherPersonalInfoComponent");
      break;
    case "collectMemberPersonalInfo":
      Component = getComponent("StepMemberPersonalInfoComponent");
      break;
    case "collectMilitaryStatus":
      Component = getComponent("StepCollectMilitaryStatusComponent");
      break;
    case "collectActiveMilitaryPersonalInfo":
      Component = getComponent("StepActiveMilitaryPersonalInfoComponent");
      break;
    case "collectInactiveMilitaryPersonalInfo":
      Component = getComponent("StepInactiveMilitaryPersonalInfoComponent");
      break;
    case "collectMilitaryPersonalInfo":
      Component = getComponent("StepCollectMilitaryPersonalInfoComponent");
      break;
    case "collectFirstResponderPersonalInfo":
      Component = getComponent("StepFirstResponderPersonalInfoComponent");
      break;
    case "collectMedicalProfessionalPersonalInfo":
      Component = getComponent("StepMedicalProfessionalPersonalInfoComponent");
      break;
    case "collectEmployeePersonalInfo":
      Component = getComponent("StepEmploymentPersonalInfoComponent");
      break;
    case "collectDriverLicensePersonalInfo":
      Component = getComponent("StepDriverLicensePersonalInfoComponent");
      break;
    case "collectGeneralIdentityPersonalInfo":
      Component = getComponent("StepGeneralIdentityPersonalInfoComponent");
      break;
    case "collectHybridIdentityPersonalInfo":
      Component = getComponent("StepHybridIdentityPersonalInfoComponent");
      break;
    case "collectLicensedProfessionalPersonalInfo":
      Component = getComponent("StepLicensedProfessionalPersonalInfoComponent");
      break;
    case "collectMoverPersonalInfo":
      Component = getComponent("StepMoverPersonalInfoComponent");
      break;
    case "collectLowIncomePersonalInfo":
      Component = getComponent("StepLowIncomePersonalInfoComponent");
      break;
    case "collectSocialSecurityNumber":
      Component = getComponent("StepSocialSecurity");
      break;
    case "success":
      Component = getComponent("StepSuccessComponent");
      break;
    case "consolation":
      Component = getComponent("StepConsolationComponent");
      break;
    case "override":
      Component = getComponent("StepOverrideComponent");
      break;
    case "docUpload":
      Component = getComponent("StepDocUploadComponent");
      break;
    case "pending":
      Component = getComponent("StepPendingComponent");
      break;
    case "sso":
      Component = getComponent("StepSSOComponent");
      break;
    case "thirdPartyLoop":
      Component = getComponent("StepThirdPartyLoopComponent");
      break;
    case "smsLoop":
      Component = getComponent("StepSMSLoopComponent");
      break;
    case "emailLoop":
      Component = getComponent("StepEmailLoopComponent");
      break;
    case "idCheckLoop":
      Component = getComponent("StepIDCheckLoopComponent");
      break;
    case "collectIdentifier":
      Component = getComponent("StepCollectIdentifierComponent");
      break;
    case "completeAuthentication":
      Component = getComponent("StepCompleteAuthentication");
      break;
    case "missingRequiredMetadata":
      Component = getComponent("StepErrorComponent");
      return (
        <Component verificationService={verificationService} errorId="missingRequiredMetadata" />
      );
    case "missingRequiredExternalId":
      Component = getComponent("StepErrorComponent");
      return (
        <Component verificationService={verificationService} errorId="missingRequiredExternalId" />
      );
    // If explicitly trying to render the error component, such as when testing/styling
    case "error":
      Component = getComponent("StepErrorComponent");
      break;
    case "cancelDocUpload":
    case "cancelEmailLoop":
    case "cancelSocialSecurityNumber":
    case "docReviewLimitExceeded":
    case "emailLoopCollectOrganizationEmail":
      // These steps are not loaded directly through this function. They exist
      // here so we get exhaustiveness type checking.
      logger.error("Attempted to load unexpected step", stepToLoad);
      return <StepErrorComponent verificationService={verificationService} errorId="invalidStep" />;
    default:
      // This 'satisfies never' ensures that all possible steps are handled.
      logger.error("Unable to load step", stepToLoad satisfies never);
      return <StepErrorComponent verificationService={verificationService} errorId="invalidStep" />;
  }

  return (
    <Suspense fallback={getLoadingScreen()}>
      <Component verificationService={verificationService} />
    </Suspense>
  );
};

export { LoadStepComponent };
